import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, X, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { logActivity } from "@/lib/activity-logger";

const COLORS = ["bg-sage-light", "bg-terracotta-light", "bg-card", "bg-secondary"];

export default function TodosPage() {
  const { user, profile } = useAuth();
  const [todos, setTodos] = useState<any[]>([]);
  const [newContent, setNewContent] = useState("");
  const [loading, setLoading] = useState(true);

  const workspaceId = profile?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;
    supabase
      .from("todos")
      .select("*")
      .eq("workspace_id", workspaceId)
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) toast.error("Couldn't load to-dos", { description: error.message });
        setTodos(data || []);
        setLoading(false);
      });
  }, [workspaceId]);

  const addTodo = async () => {
    if (!newContent.trim() || !workspaceId) return;
    try {
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const { data, error } = await supabase
        .from("todos")
        .insert({ workspace_id: workspaceId, created_by: user!.id, content: newContent.trim(), color })
        .select()
        .single();
      if (error) { toast.error("Couldn't add", { description: error.message }); return; }
      setTodos([data, ...todos]);
      setNewContent("");
      logActivity("todo_created", { todo_id: data.id });
    } catch (e: any) {
      toast.error("Couldn't add", { description: e.message });
    }
  };

  const toggleTodo = async (id: string, isDone: boolean) => {
    await supabase.from("todos").update({ is_done: !isDone }).eq("id", id);
    setTodos(todos.map((t) => (t.id === id ? { ...t, is_done: !isDone } : t)));
    if (!isDone) logActivity("todo_completed", { todo_id: id });
  };

  const deleteTodo = async (id: string) => {
    await supabase.from("todos").delete().eq("id", id);
    setTodos(todos.filter((t) => t.id !== id));
  };

  return (
    <div className="space-y-8 max-w-full">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-3xl md:text-4xl font-display font-semibold text-foreground">Shared To-Dos</h1>
        <p className="mt-2 text-muted-foreground text-lg">Quick notes and things to remember together.</p>
      </motion.div>

      <div className="flex gap-2">
        <input value={newContent} onChange={(e) => setNewContent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTodo()} placeholder="Add a quick note..." className="flex-1 min-w-0 bg-card border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        <button onClick={addTodo} disabled={!newContent.trim()} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50 flex-shrink-0">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading to-dos...</span>
        </div>
      ) : (
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          <AnimatePresence>
            {todos.map((todo) => (
              <motion.div key={todo.id} layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className={`${todo.color || "bg-card"} border border-border rounded-xl p-4 break-inside-avoid group relative`}>
                <div className="flex items-start gap-3">
                  <button onClick={() => toggleTodo(todo.id, todo.is_done)} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${todo.is_done ? "bg-primary border-primary" : "border-muted-foreground"}`}>
                    {todo.is_done && <Check className="w-3 h-3 text-primary-foreground" />}
                  </button>
                  <p className={`text-sm leading-relaxed break-words min-w-0 ${todo.is_done ? "line-through text-muted-foreground" : "text-foreground"}`}>{todo.content}</p>
                </div>
                <button onClick={() => deleteTodo(todo.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-secondary">
                  <X className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
